// Port of SuiteImport/ScappleImporter.swift: a Scapple board (`.scap`, XML) as a mind map, the same rules and numbers,
// pinned by `fixtures/scapple` (`board.json` is written by the Swift test, `test/import/scapple.test.ts` compares). Notes
// keep their places and widths, scaled so the board's text size reads as the map's normal size; connections become lines,
// `PointsToNoteIDs` arrows; borders become outlines and colours inks; bold over a whole note becomes a bold note;
// background shapes become regions. Ids are derived from the board's, so importing the same board twice makes the same
// notes.
import type { InkColor } from '../contract.ts';
import { stableUUID } from '../hashing.ts';
import { emptyMapContent, mapLine, mapNote, mapRegion, type MapArrow, type MapContent, type MapOutline, type MapPoint } from '../maps/map.ts';
import { charIsWhitespace, trimCFWhitespace, trimCFWhitespaceAndNewlines } from '../unicode.ts';
import { parseXML, XMLError, type XMLNode } from './xmlTree.ts';

export interface ScappleReport {
  notes: number;
  lines: number;
  regions: number;
  /** Notes with bold, italic or other styling on part of their words: kept as plain words. */
  partlyFormatted: number;
  /** Notes with a shadow, which a map doesn't draw. */
  shadows: number;
}

/** `ScappleImport.Report.summary`: "9 notes, 6 lines and 1 region. …". */
export function scappleSummary(report: ScappleReport): string {
  const { notes, lines, regions, partlyFormatted, shadows } = report;
  let counts = `${notes} ${notes === 1 ? 'note' : 'notes'}, ${lines} ${lines === 1 ? 'line' : 'lines'}`;
  if (regions > 0) counts += ` and ${regions} ${regions === 1 ? 'region' : 'regions'}`;
  const sentences = [counts + '.'];
  if (partlyFormatted > 0) {
    sentences.push(`Bold or italic on part of ${partlyFormatted === 1 ? '1 note' : `${partlyFormatted} notes`} isn't kept; the words are.`);
  }
  if (shadows > 0) sentences.push(`Shadows (${shadows === 1 ? '1 note' : `${shadows} notes`}) aren't drawn on a map.`);
  return sentences.join(' ');
}

/** The map's normal note size, which the board's most common text size is scaled to. */
const NORMAL_SIZE = 17;

/** Swift's `Double(String)` for decimals (no surrounding spaces); "inf" and "nan" too. */
function swiftDouble(text: string | undefined): number | null {
  if (text === undefined) return null;
  if (/^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/.test(text)) return Number(text);
  if (/^[+-]?(inf|infinity)$/i.test(text)) return text.startsWith('-') ? -Infinity : Infinity;
  if (/^[+-]?nan$/i.test(text)) return NaN;
  return null;
}

/** Swift's `Int(String)`. */
function swiftInt(text: string): number | null {
  return /^[+-]?\d+$/.test(text) ? parseInt(text, 10) : null;
}

/** `trimmingCharacters(in: .whitespaces)` and `(in: .whitespacesAndNewlines)`. */
const trimSpaces = trimCFWhitespace;
const trimAll = trimCFWhitespaceAndNewlines;

/** "6-10,12" → 6, 7, 8, 9, 10, 12. */
export function scappleIds(list: string | undefined | null): string[] {
  return (list ?? '').split(',').filter((p) => p !== '').flatMap((part) => {
    const bounds = part.split('-').filter((p) => p !== '').flatMap((b) => {
      const n = swiftInt(trimSpaces(b));
      return n === null ? [] : [n];
    });
    if (bounds.length === 2 && bounds[0] <= bounds[1] && bounds[1] - bounds[0] < 10_000) {
      return Array.from({ length: bounds[1] - bounds[0] + 1 }, (_, i) => String(bounds[0] + i));
    }
    return bounds.length === 1 ? [String(bounds[0])] : [];
  });
}

function point(text: string | undefined): MapPoint | null {
  const parts = (text ?? '').split(',').filter((p) => p !== '').flatMap((p) => {
    const n = swiftDouble(trimSpaces(p));
    return n === null ? [] : [n];
  });
  return parts.length === 2 ? { x: parts[0], y: parts[1] } : null;
}

/** To a tenth, halves away from zero (Swift's `rounded()`). */
const tenth = (v: number) => (Math.sign(v * 10) * Math.round(Math.abs(v * 10))) / 10 + 0;

/** A Scapple colour ("r g b", 0–1) as an ink (`ScappleColor.ink`): the hue picks it, as Scrivener's comment colours are
 *  read, with yellow kept; a washed-out or black colour is charcoal (null). */
export function scappleColorInk(color: string | undefined | null): InkColor | null {
  if (color === undefined || color === null) return null;
  const parts = color.split(/[ ,]/).filter((p) => p !== '').flatMap((p) => {
    const n = swiftDouble(p);
    return n === null ? [] : [n];
  });
  if (parts.length < 3) return null;
  const clamp = (v: number) => Math.min(Math.max(v, 0), 1);
  const r = clamp(parts[0]), g = clamp(parts[1]), b = clamp(parts[2]);
  const hi = Math.max(r, g, b), lo = Math.min(r, g, b);
  if (!(hi > 0 && (hi - lo) / hi >= 0.18)) return null;
  let hue: number;
  if (hi === r) hue = 60 * ((g - b) / (hi - lo));
  else if (hi === g) hue = 60 * (2 + (b - r) / (hi - lo));
  else hue = 60 * (4 + (r - g) / (hi - lo));
  if (hue < 0) hue += 360;
  if (hue < 15 || hue >= 345) return 'red';
  if (hue < 45) return 'orange';
  if (hue < 75) return 'yellow';
  if (hue < 170) return 'green';
  if (hue < 260) return 'blue';
  if (hue < 300) return 'purple';
  return 'pink';
}

/** Whether the note is bold throughout (one bold range over all its words), and whether any styling covers only part.
 *  Ranges are UTF-16, as Cocoa counts; trailing white space isn't part of the words. */
function formatting(node: XMLNode | undefined, text: string): { bold: boolean; partly: boolean } {
  const ranges = node?.childrenNamed('FormatRange') ?? [];
  if (!ranges.length) return { bold: false, partly: false };
  const scalars = [...text];
  let trailing = 0;
  for (let i = scalars.length - 1; i >= 0 && charIsWhitespace(scalars[i]); i--) trailing += scalars[i].length;
  const words = text.length - trailing;
  let bold = false, partly = false;
  for (const range of ranges) {
    const parts = range.text.split(',').filter((p) => p !== '').flatMap((p) => {
      const n = swiftInt(trimAll(p));
      return n === null ? [] : [n];
    });
    if (parts.length !== 2) continue;
    const styled = Object.entries(range.attributes).filter(([, v]) => v.toLowerCase() === 'yes').map(([k]) => k);
    if (!styled.length) continue;
    if (parts[0] === 0 && parts[1] >= words && styled.every((k) => k === 'Bold')) bold = true;
    else partly = true;
  }
  return { bold, partly };
}

/** Reads a board (`ScappleImport.content(from:date:)`). `date` is when the notes count as made (in board order, a second
 *  apart: stored times are whole seconds). */
export function scappleImport(data: Uint8Array | string, date: Date = new Date()): { content: MapContent; report: ScappleReport } {
  const root = parseXML(data);
  if (root.name !== 'ScappleDocument') throw new XMLError('not a Scapple board');
  const board = root.attr('ID') ?? '';
  const padding = swiftDouble(root.child('UISettings')?.value('NoteXPadding') ?? '') ?? 8;
  const autoFit = new Set(scappleIds(root.value('AutoFit')));
  const noteNodes = root.child('Notes')?.childrenNamed('Note') ?? [];
  const shapeNodes = root.child('BackgroundShapes')?.children ?? [];

  // Scaled so the board's usual text size is the map's normal size.
  const sizes = noteNodes.map((n) => swiftDouble(n.attr('FontSize') ?? '') ?? 12).sort((a, b) => a - b);
  const median = sizes.length ? sizes[Math.floor((sizes.length - 1) / 2)] : 12;
  const scale = Math.min(2, Math.max(1, NORMAL_SIZE / Math.max(median, 1)));
  const places = [...noteNodes, ...shapeNodes].flatMap((n) => point(n.attr('Position')) ?? []);
  const minX = places.length ? Math.min(...places.map((p) => p.x)) : 0;
  const minY = places.length ? Math.min(...places.map((p) => p.y)) : 0;
  const at = (p: MapPoint): MapPoint => ({ x: tenth((p.x - minX) * scale), y: tenth((p.y - minY) * scale) });

  const report: ScappleReport = { notes: 0, lines: 0, regions: 0, partlyFormatted: 0, shadows: 0 };
  const content = emptyMapContent();
  const noteIDs = new Map<string, string>();
  const connected = new Map<string, string[]>();
  const pointsTo = new Map<string, Set<string>>();
  noteNodes.forEach((node, index) => {
    const id = node.attr('ID');
    if (id === undefined) return;
    const mapID = stableUUID(`scapple-note:${board}:${id}`);
    noteIDs.set(id, mapID);
    connected.set(id, scappleIds(node.value('ConnectedNoteIDs')));
    pointsTo.set(id, new Set(scappleIds(node.value('PointsToNoteIDs'))));
    const text = node.child('String')?.text ?? '';
    const fontSize = swiftDouble(node.attr('FontSize') ?? '') ?? 12;
    const appearance = node.child('Appearance');
    const border = appearance?.child('Border');
    let outline: MapOutline = 'none';
    if (border && (swiftDouble(border.attr('Weight') ?? '1') ?? 1) > 0) {
      const style = (border.attr('Style') ?? '').toLowerCase();
      outline = style === 'cloud' || style === 'jagged' ? 'cloud' : 'box';
    }
    const ink = scappleColorInk(appearance?.value('TextColor'))
      ?? scappleColorInk(border ? trimAll(border.text) : undefined)
      ?? scappleColorInk(appearance?.value('Fill'));
    const { bold, partly } = formatting(node.child('Formatting'), text);
    if (partly) report.partlyFormatted += 1;
    if (node.attr('Shadow')?.toLowerCase() === 'yes') report.shadows += 1;
    const width = swiftDouble(node.attr('Width') ?? '');
    content.notes.push(mapNote({
      id: mapID, text, at: at(point(node.attr('Position')) ?? { x: minX, y: minY }),
      width: autoFit.has(id) || width === null ? null : tenth(Math.max(30, (width - padding * 2) * scale)),
      outline, ink, size: fontSize < 11 ? 'small' : fontSize < 18 ? 'normal' : 'large', bold, face: 'serif',
      createdAt: new Date(date.getTime() + index * 1000),
    }));
  });

  // Each pair once, from the lower id; an arrow where a note points at the other.
  const pairs = new Map<string, [number, number]>();
  for (const [a, others] of connected) {
    for (const b of new Set([...others, ...(pointsTo.get(a) ?? [])])) {
      const x = swiftInt(a), y = swiftInt(b);
      if (x === null || y === null || x === y || !noteIDs.has(a) || !noteIDs.has(b)) continue;
      const pair: [number, number] = [Math.min(x, y), Math.max(x, y)];
      pairs.set(pair.join(','), pair);
    }
  }
  for (const [x, y] of [...pairs.values()].sort((p, q) => p[0] - q[0] || p[1] - q[1])) {
    const lo = String(x), hi = String(y);
    const forward = pointsTo.get(lo)?.has(hi) ?? false, back = pointsTo.get(hi)?.has(lo) ?? false;
    const [from, to] = back && !forward ? [hi, lo] : [lo, hi];
    const fromID = noteIDs.get(from), toID = noteIDs.get(to);
    if (fromID === undefined || toID === undefined) continue;
    const arrow: MapArrow = forward && back ? 'both' : forward || back ? 'end' : 'none';
    content.lines.push(mapLine({ id: stableUUID(`scapple-line:${board}:${lo}-${hi}`), from: fromID, to: toID, arrow }));
  }

  shapeNodes.forEach((shape, index) => {
    const p = point(shape.attr('Position'));
    const w = swiftDouble(shape.attr('Width') ?? ''), h = swiftDouble(shape.attr('Height') ?? '');
    if (!p || w === null || h === null) return;
    const appearance = shape.child('Appearance');
    const fill = appearance?.value('Fill') ?? shape.value('Fill');
    const borderNode = appearance?.child('Border');
    content.regions.push(mapRegion({
      id: stableUUID(`scapple-shape:${board}:${shape.attr('ID') ?? String(index)}`), at: at(p),
      width: tenth(w * scale), height: tenth(h * scale),
      ink: scappleColorInk(fill) ?? scappleColorInk(borderNode ? trimAll(borderNode.text) : undefined) ?? 'grey',
      label: trimAll(shape.child('String')?.text ?? ''),
    }));
  });
  content.regions.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  report.notes = content.notes.length;
  report.lines = content.lines.length;
  report.regions = content.regions.length;
  return { content, report };
}
